import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useLocalState } from "./LocalStateProvider";
import * as Sentry from "@sentry/react"

const AudioPlayerContext = createContext();
export const AudioPlayerProvider = ({ children }) => {
  const [audioSrc, setAudioSrc] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageSrc, setImageSrc] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1); // default volume
  const [isBuffering, setIsBuffering] = useState(false); // New state for buffering
  const audioRef = useRef(null);
  const [streamingSrc, setStreamingSrc] = useState(null);
  const [aiResponseId, setAiResponseId] = useState(null);
  const [discoverId, setDiscoverId] = useState(null);
  const { localState, updateLocalState } = useLocalState();

  // Detect environment for audio playback handling
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  const isPowerPointEnvironment = () => {
    return window.Office && window.Office.context && window.Office.context.platform;
  };
  const isMacPowerPoint = isMac && isPowerPointEnvironment();

  // Audio context to help with autoplay restrictions on Mac PowerPoint
  const audioContextRef = useRef(null);
  
  // Initialize audio context for Mac PowerPoint to handle autoplay policies
  useEffect(() => {
    if (isMacPowerPoint && !audioContextRef.current) {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          audioContextRef.current = new AudioContext();
          console.log('[AudioPlayerContext] Mac PowerPoint: Audio context initialized for autoplay compatibility');
        }
      } catch (error) {
        console.log('[AudioPlayerContext] Mac PowerPoint: Could not initialize audio context:', error.message);
      }
    }
  }, [isMacPowerPoint]);

  const convertDurationToSeconds = (durationStr) => {
    const parts = durationStr.split(":");
    let seconds = 0;
    if (parts.length === 2) {
      // mm:ss format
      const [minutes, secs] = parts;
      seconds = parseInt(minutes) * 60 + parseFloat(secs);
    } else if (parts.length === 3) {
      // hh:mm:ss format (optional)
      const [hours, minutes, secs] = parts;
      seconds =
        parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseFloat(secs);
    }
    return seconds;
  };  const playAudio = (src, title, description, imageSrc, passedDuration) => {
    console.log(`[AudioPlayerContext] playAudio called with src: ${src}, title: ${title}`);
    
    if (audioRef.current) {
      console.log(`[AudioPlayerContext] Cleaning up previous audio instance`);
      audioRef.current.pause();
      audioRef.current.removeEventListener("timeupdate", handleTimeUpdate);
      audioRef.current.removeEventListener(
        "loadedmetadata",
        handleLoadedMetadata
      );
      audioRef.current.removeEventListener("error", handleError);
      audioRef.current.removeEventListener("ended", handleEnded);
      audioRef.current.removeEventListener("waiting", handleWaiting); // Arsh multigen change
      audioRef.current.removeEventListener("playing", handlePlaying); // Arsh multigen change
    }

    // Create a new Audio instance for streaming
    audioRef.current = new Audio(src);
    audioRef.current.volume = volume; // set initial volume
    
    // Mac PowerPoint specific audio configuration
    if (isMacPowerPoint) {
      console.log(`[AudioPlayerContext] Mac PowerPoint: Configuring audio for optimal playback`);
      // More aggressive preloading for Mac PowerPoint
      audioRef.current.preload = "auto";
      // Set load strategy for better compatibility
      audioRef.current.load();
    } else {
      // Set the preload attribute to ensure audio is fetched immediately
      audioRef.current.preload = "auto";
    }
    
    // Always reset current time to 0 when playing a new track
    console.log(`[AudioPlayerContext] Resetting currentTime to 0 for new track`);
    setCurrentTime(0);
    audioRef.current.currentTime = 0;
    
    // Force position to beginning before starting playback
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        console.log(`[AudioPlayerContext] Double-checking currentTime is 0 (actual: ${audioRef.current.currentTime})`);
      }
    }, 10);
    
    // Add event listeners
    audioRef.current.addEventListener("timeupdate", handleTimeUpdate);
    audioRef.current.addEventListener("ended", handleEnded);

    audioRef.current.addEventListener("error", handleError);
    if (typeof passedDuration === "string") {
      passedDuration = convertDurationToSeconds(passedDuration);
    }

    if (Number.isFinite(passedDuration) && passedDuration > 0) {
      // If a valid duration is passed, use it and do not attach the loadedmetadata event listener
      setDuration(passedDuration);
    } else {
      // If no valid duration is passed, rely on the loadedmetadata event
      audioRef.current.addEventListener("loadedmetadata", handleLoadedMetadata);
    }

    // Arsh multigen change
    const handleWaiting = () => {
      setIsBuffering(true); // Start buffering indicator
    };
    // Arsh multigen change
    const handlePlaying = () => {
      setIsBuffering(false); // Stop buffering indicator
    };    
    
    audioRef.current.addEventListener("waiting", handleWaiting); // Add listener for buffering
    audioRef.current.addEventListener("playing", handlePlaying); // Add listener for buffering end    // Always reset position for all browsers, not just Safari
    console.log(`[AudioPlayerContext] Setting currentTime to 0 before playback`);
    setCurrentTime(0);
    audioRef.current.currentTime = 0;
    
    // Add a one-time "loadeddata" event to ensure we start from beginning
    const handleLoadedData = () => {
      console.log(`[AudioPlayerContext] Audio data loaded, forcing currentTime to 0`);
      audioRef.current.currentTime = 0;
      audioRef.current.removeEventListener('loadeddata', handleLoadedData);
    };
    audioRef.current.addEventListener('loadeddata', handleLoadedData);

    // Start playback after ensuring position is 0
    console.log(`[AudioPlayerContext] Starting playback from position: ${audioRef.current.currentTime}`);
    
    // Mac PowerPoint specific handling for audio playback
    if (isMacPowerPoint) {
      console.log(`[AudioPlayerContext] Mac PowerPoint detected - using enhanced audio playback strategy`);
      
      // Try to play with enhanced error handling and retries for Mac PowerPoint
      const attemptPlayback = async (retryCount = 0) => {
        try {
          // Wait for audio to be sufficiently loaded on Mac PowerPoint
          await new Promise(resolve => {
            if (audioRef.current.readyState >= 2) { // HAVE_CURRENT_DATA
              resolve();
            } else {
              const handleCanPlay = () => {
                audioRef.current.removeEventListener('canplay', handleCanPlay);
                resolve();
              };
              audioRef.current.addEventListener('canplay', handleCanPlay);
            }
          });
          
          // Resume audio context if needed on Mac PowerPoint
          if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            console.log(`[AudioPlayerContext] Mac PowerPoint: Resuming audio context`);
            await audioContextRef.current.resume();
          }
          
          // Try to play the audio
          await audioRef.current.play();
          console.log(`[AudioPlayerContext] Mac PowerPoint: Audio playback started successfully`);
          
        } catch (error) {
          console.log(`[AudioPlayerContext] Mac PowerPoint: Playback attempt ${retryCount + 1} failed:`, error.message);
          
          if (error.name === 'NotAllowedError' && retryCount === 0) {
            // For autoplay restrictions, try again after a short delay
            console.log(`[AudioPlayerContext] Mac PowerPoint: Autoplay blocked, retrying after user interaction simulation`);
            setTimeout(() => attemptPlayback(retryCount + 1), 100);
          } else if (retryCount < 2) {
            // General retry with delay for Mac PowerPoint
            console.log(`[AudioPlayerContext] Mac PowerPoint: Retrying playback in 200ms`);
            setTimeout(() => attemptPlayback(retryCount + 1), 200);
          } else {
            console.error(`[AudioPlayerContext] Mac PowerPoint: Failed to play audio after ${retryCount + 1} attempts:`, error);
            Sentry.captureException(error);
          }
        }
      };
      
      attemptPlayback();
    } else {
      // Standard playback for other environments
      audioRef.current.play().catch((error) => {
        console.error("Error playing audio:", error);
        Sentry.captureException(error);
      });
    }

    // Update state
    setAudioSrc(src);
    setStreamingSrc(src); // Arsh multigen change
    setTitle(title);
    setDescription(description);
    setImageSrc(imageSrc);
    setIsPlaying(true);
    updateLocalState("isAudioPlaying", true);
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };
  const resumeAudio = () => {
    if (audioRef.current) {
      console.log(`[AudioPlayerContext] Resuming audio from position: ${audioRef.current.currentTime}`);

      // Mac PowerPoint specific handling for resuming audio
      if (isMacPowerPoint) {
        console.log(`[AudioPlayerContext] Mac PowerPoint: Using enhanced resume strategy`);
        
        const attemptResume = async (retryCount = 0) => {
          try {
            // Resume audio context if needed on Mac PowerPoint
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
              console.log(`[AudioPlayerContext] Mac PowerPoint: Resuming audio context for resume`);
              await audioContextRef.current.resume();
            }
            
            await audioRef.current.play();
            console.log(`[AudioPlayerContext] Mac PowerPoint: Audio resumed successfully`);
            setIsPlaying(true);
          } catch (error) {
            console.log(`[AudioPlayerContext] Mac PowerPoint: Resume attempt ${retryCount + 1} failed:`, error.message);
            
            if (retryCount < 2) {
              console.log(`[AudioPlayerContext] Mac PowerPoint: Retrying resume in 100ms`);
              setTimeout(() => attemptResume(retryCount + 1), 100);
            } else {
              console.error(`[AudioPlayerContext] Mac PowerPoint: Failed to resume audio after ${retryCount + 1} attempts:`, error);
              Sentry.captureException(error);
            }
          }
        };
        
        attemptResume();
      } else {
        // Standard resume for other environments
        audioRef.current.play().catch((error) => {
          console.error("Error resuming audio:", error);
          Sentry.captureException(error);
        });
        setIsPlaying(true);
      }
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // reset playback position
      audioRef.current = null;
      setAudioSrc(null);
      setTitle("");
      setDescription("");
      setImageSrc("");
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setAiResponseId(null);
      setDiscoverId(null);
      setIsBuffering(false); // Arsh multigen change

      updateLocalState("isAudioPlaying", false);
      // setIsBuffering(false); // Stop buffering indicator when stopped
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };
  // Track if this is the first update after starting a new track
  const isFirstTimeUpdateRef = useRef(true);
  
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const currentAudioTime = audioRef.current.currentTime;
      
      // Special handling for the first timeupdate event
      if (isFirstTimeUpdateRef.current && currentAudioTime > 0.1) {
        console.log(`[AudioPlayerContext] First timeupdate after new track, current time: ${currentAudioTime}`);
        console.log(`[AudioPlayerContext] Forcing first position update to 0`);
        
        // Only force to 0 if we're at the beginning of a track (first 3 seconds)
        if (currentAudioTime < 3) {
          audioRef.current.currentTime = 0;
          setCurrentTime(0);
        }
        
        isFirstTimeUpdateRef.current = false;
      } else {
        setCurrentTime(currentAudioTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;

      // Check if audioDuration is a valid number
      if (Number.isFinite(audioDuration) && audioDuration > 0) {
        // Use audioRef's duration if it's valid and greater than 0
        setDuration(audioDuration);
      }
    }
  };

  useEffect(() => {
    if (localState.audioImgSrc !== null) setImageSrc(localState.audioImgSrc);
  }, [localState.audioImgSrc]);

  const handleError = (error) => {
    console.error("Audio error:", error);
    // Implement further error handling if needed
    // setIsBuffering(false); // Stop buffering indicator on error
  };

  // const handleWaiting = () => {
  //   setIsBuffering(true); // Start buffering indicator
  // };

  // const handlePlaying = () => {
  //   setIsBuffering(false); // Stop buffering indicator
  // };
  const seekAudio = (time) => {
    if (isBuffering) return;

    if (audioRef.current) {
      // Ensure time doesn't exceed the duration
      const safeTime = Math.min(time, duration);

      // Ensure time is not negative
      const clampedTime = Math.max(0, safeTime);
      
      console.log(`[AudioPlayerContext] Seeking to time: ${clampedTime}s`);
      
      // Special handling for very small seek values (less than 0.5s) - likely a new track
      if (clampedTime < 0.5 && audioRef.current.currentTime > 1) {
        console.log(`[AudioPlayerContext] Detected seek to beginning, forcing to exactly 0`);
        audioRef.current.currentTime = 0;
        setCurrentTime(0);
      } else {
        audioRef.current.currentTime = clampedTime;
        setCurrentTime(clampedTime);
      }
    }
  };

  const changeVolume = (newVolume) => {
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setVolume(newVolume);
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{
        audioSrc,
        setImageSrc,
        title,
        description,
        imageSrc,
        isPlaying,
        currentTime,
        duration,
        volume,
        isBuffering,
        streamingSrc,
        setStreamingSrc,
        setAiResponseId,
        setDiscoverId,
        aiResponseId,
        discoverId,
        setIsBuffering,
        playAudio,
        pauseAudio,
        resumeAudio,
        stopAudio,
        seekAudio,
        changeVolume,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => useContext(AudioPlayerContext);
